/**
 * Slovak domestic accounts: format and mod11 (the CZ weights); the bank code is not checked
 * against a list. Always convertible to IBAN.
 * @module banking/schemes/sk
 */

import { CountryCode } from 'ibankit';
import { generateIBAN } from '../generation';
import { mod11 } from '../validation';
import { BankAccountScheme, type BankAccountSchemeDefinition } from './bank-account-scheme';
import {
  checkDomesticAccount,
  formatDomesticNumber,
  parseDomesticAccount
} from './domestic-account';
import { bicErrors, normalizeBic } from './scheme-helpers';

const COUNTRY = 'SK';

export const skScheme: BankAccountSchemeDefinition = {
  scheme: BankAccountScheme.SK,

  validate(account) {
    const { errors, parts } = checkDomesticAccount(account, COUNTRY);
    if (parts?.prefix && !mod11(parts.prefix)) {
      errors.push({ code: 'prefixMod11Failed', message: 'Account prefix fails mod11 check' });
    }
    if (parts && !mod11(parts.number)) {
      errors.push({ code: 'accountMod11Failed', message: 'Account number fails mod11 check' });
    }
    errors.push(...bicErrors(account.bic));

    return { valid: errors.length === 0, errors };
  },

  normalize(account) {
    const parts = parseDomesticAccount(account);
    if (!parts) return null;

    return {
      scheme: BankAccountScheme.SK,
      countryCode: COUNTRY,
      iban: null,
      bankIdentifier: parts.bankCode,
      accountNumber: formatDomesticNumber(parts),
      bic: normalizeBic(account.bic)
    };
  },

  toIban(account) {
    const parts = parseDomesticAccount(account);
    if (!parts) return null;

    // SK BBAN: bank code (4) + prefix (6) + number (10); ibankit treats prefix + number as one.
    const bban = (parts.prefix ?? '0').padStart(6, '0') + parts.number.padStart(10, '0');
    return generateIBAN(CountryCode.SK, parts.bankCode, bban);
  }
};
